/* One confirmed order note for the cart page and drawer. */
(function (root, factory) {
  'use strict';
  var api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (root.document && !root.MASCartNote) root.MASCartNote = api.install(root);
}(typeof window === 'undefined' ? globalThis : window, function () {
  'use strict';

  function createStore(initial, save, notify, delay) {
    var desired = initial, confirmed = initial, uncertain = false;
    var flight = null, timer = null, status = 'idle';
    function read() {
      return { value: desired, confirmed: confirmed, status: status,
        pending: !!flight || uncertain || desired !== confirmed };
    }
    function changed() { notify(read()); }
    function cancelTimer() { clearTimeout(timer); timer = null; }
    function flush() {
      cancelTimer();
      if (flight) return flight;
      if (!uncertain && desired === confirmed) return Promise.resolve();
      status = 'saving';
      // Start in a microtask so all callers share the same in-flight promise.
      flight = Promise.resolve().then(async function () {
        while (uncertain || desired !== confirmed) {
          var value = desired;
          await save(value);
          confirmed = value;
          uncertain = false;
        }
        status = 'saved';
      }).catch(function (error) {
        // A lost response may have reached Shopify. Retry even if the customer
        // has since restored the original value (including an empty note).
        uncertain = true;
        status = 'error';
        throw error;
      }).finally(function () { flight = null; changed(); });
      changed();
      return flight;
    }
    function edit(value) {
      if (value === desired) return;
      desired = value;
      status = flight ? 'saving' : 'unsaved';
      cancelTimer();
      timer = setTimeout(function () { flush().catch(function () {}); }, delay == null ? 250 : delay);
      changed();
    }
    function remote(value) {
      if (read().pending) return;
      desired = confirmed = value;
      changed();
    }
    return { read: read, edit: edit, flush: flush, remote: remote };
  }

  function install(win) {
    var doc = win.document;
    var selector = 'hdt-cart-note textarea[name="note"]';
    var first = doc.querySelector(selector);
    var store, banner, statusId = 0;
    var replayed = new WeakSet(), submitting = new WeakSet(), waiting = new WeakMap();
    var route = (win.Shopify && win.Shopify.routes && win.Shopify.routes.root) || '/';

    async function save(value) {
      var controller = new win.AbortController();
      var timeout = win.setTimeout(function () { controller.abort(); }, 12000);
      try {
        var response = await win.fetch(route + 'cart/update.js', {
          method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ note: value }), keepalive: true, signal: controller.signal
        });
        if (!response.ok) throw new Error('Order note save failed');
        var cart = await response.json();
        var actual = cart.note == null ? '' : cart.note;
        if (typeof actual !== 'string' || actual.replace(/\r\n/g, '\n') !== value.replace(/\r\n/g, '\n')) {
          throw new Error('Order note was not confirmed');
        }
      } finally { win.clearTimeout(timeout); }
    }
    function setText(node, text) { if (node.textContent !== text) node.textContent = text; }
    function render(state) {
      var message = state.status === 'error' ? 'Your order note could not be saved. Please retry.' :
        state.status === 'saving' || state.status === 'unsaved' ? 'Saving order note…' :
        state.status === 'saved' ? 'Order note saved.' : '';
      doc.querySelectorAll(selector).forEach(function (field) {
        if (field.value !== state.value) field.value = state.value;
        var host = field.closest('hdt-cart-note');
        var status = host.querySelector('[data-mas-note-status]');
        if (!status) {
          status = doc.createElement('p');
          status.id = 'mas-note-status-' + (++statusId);
          status.className = 'mas-note-status';
          status.setAttribute('data-mas-note-status', '');
          status.setAttribute('role', 'status');
          status.setAttribute('aria-live', 'polite');
          field.insertAdjacentElement('afterend', status);
          field.setAttribute('aria-describedby', [field.getAttribute('aria-describedby'), status.id].filter(Boolean).join(' '));
        }
        status.hidden = !message;
        status.setAttribute('data-state', state.status);
        setText(status, message);
      });
      doc.querySelectorAll('[data-label-note]').forEach(function (label) {
        if (label.children.length !== 2) return;
        label.firstElementChild.hidden = state.value.length > 0;
        label.lastElementChild.hidden = state.value.length === 0;
      });
      if (state.status === 'error' && !banner) {
        banner = doc.createElement('div');
        banner.className = 'mas-note-error';
        banner.setAttribute('role', 'alert');
        var text = doc.createElement('p');
        text.textContent = 'Your order note has not been saved. Retry saving before continuing.';
        var retry = doc.createElement('button');
        retry.type = 'button';
        retry.textContent = 'Retry saving note';
        retry.addEventListener('click', function () { store.flush().catch(function () {}); });
        banner.append(text, retry);
        doc.body.appendChild(banner);
      }
      if (banner) banner.hidden = state.status !== 'error';
    }
    store = createStore(first ? first.value : '', save, render);
    function noteField(target) { return target && target.matches && target.matches(selector); }
    function readFocused() {
      if (noteField(doc.activeElement)) store.edit(doc.activeElement.value);
    }
    function stop(event) { event.preventDefault(); event.stopImmediatePropagation(); }
    function gate(key, action, failed) {
      if (waiting.has(key)) return;
      var task = store.flush().then(action).catch(function () {
        if (failed) failed();
        doc.dispatchEvent(new win.CustomEvent('theme:loading:done', { bubbles: true }));
      }).finally(function () { waiting.delete(key); });
      waiting.set(key, task);
    }

    doc.addEventListener('input', function (event) {
      if (noteField(event.target)) store.edit(event.target.value);
    }, true);
    doc.addEventListener('change', function (event) {
      if (noteField(event.target)) {
        // Suppress the legacy fire-and-forget change handler. There must be
        // exactly one writer or a slower old request can overwrite a new note.
        event.stopImmediatePropagation();
        store.edit(event.target.value);
        store.flush().catch(function () {});
        return;
      }
      if (replayed.has(event) || !event.target.hasAttribute('will-change-cart')) return;
      readFocused();
      if (!store.read().pending) return;
      stop(event);
      var target = event.target;
      var control = event.detail && event.detail.target ? event.detail.target : target;
      var detail = event.detail;
      gate(control, function () {
        if (!target.isConnected || !control.isConnected) return;
        var next = new win.CustomEvent('change', { bubbles: true, detail: detail });
        replayed.add(next);
        target.dispatchEvent(next);
      }, function () {
        if (control.matches('input[type="number"]')) control.value = control.defaultValue;
      });
    }, true);

    function cartForm(form) {
      if (!form || form.tagName !== 'FORM') return false;
      var url = new win.URL(form.action, win.location.href);
      return url.origin === win.location.origin && /\/cart\/?$/.test(url.pathname);
    }
    function syncForm(form) {
      if (form.querySelector('textarea[name="note"]')) return;
      var field = form.querySelector('input[name="note"]');
      if (!field) {
        field = doc.createElement('input');
        field.type = 'hidden'; field.name = 'note'; form.appendChild(field);
      }
      field.value = store.read().value;
    }
    doc.addEventListener('submit', function (event) {
      var form = event.target;
      if (!cartForm(form) || submitting.has(form)) return;
      readFocused();
      syncForm(form);
      if (!store.read().pending) return;
      stop(event);
      var submitter = event.submitter;
      gate(form, function () {
        if (!form.isConnected) return;
        syncForm(form);
        submitting.add(form);
        try { form.requestSubmit(submitter && submitter.isConnected ? submitter : undefined); }
        finally { submitting.delete(form); }
      });
    }, true);
    doc.addEventListener('click', function (event) {
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      var link = event.target.closest && event.target.closest('a[href]');
      if (!link || link.hasAttribute('download') || (link.target && link.target !== '_self')) return;
      var url = new win.URL(link.href, win.location.href);
      if (url.origin !== win.location.origin || !/\/(cart|checkout)\/?$/.test(url.pathname)) return;
      readFocused();
      if (!store.read().pending) return;
      stop(event);
      gate(link, function () { win.location.assign(url.href); });
    }, true);
    doc.addEventListener('cart:change', function (event) {
      var cart = event.detail && event.detail.cart;
      if (cart && 'note' in cart) store.remote(cart.note || '');
    }, true);
    function refresh() { render(store.read()); }
    ['cart:drawer:render', 'cart:page:render', 'shopify:section:load'].forEach(function (name) {
      doc.addEventListener(name, refresh);
    });
    var observer = new win.MutationObserver(function (mutations) {
      if (mutations.some(function (mutation) {
        return Array.from(mutation.addedNodes).some(function (node) {
          return node.nodeType === 1 && (node.matches(selector) || node.querySelector(selector));
        });
      })) refresh();
    });
    observer.observe(doc.body, { childList: true, subtree: true });
    win.addEventListener('pagehide', function () { store.flush().catch(function () {}); });
    refresh();
    return { flush: store.flush, getState: store.read, version: '2026-09-27' };
  }
  return { createStore: createStore, install: install };
}));
